import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
interface HeroSectionProps {
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string) => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  const navigate = useNavigate();
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState();
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program
      });
    }
  }, [areaStudy, program, formData.areaOfStudy, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization);
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value);
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  // Resolve display labels for trust signals
  const selectedDegree = degreeInterestOptions.find(d => d.value === formData.degreeInterest);
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  }
  return <div className="relative bg-navy overflow-hidden" data-id="element-215">
      {/* Abstract Background */}
      <div className="absolute inset-0 z-0" data-id="element-216">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-navy-light/30 blur-3xl" data-id="element-217" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-crimson/10 blur-3xl translate-x-1/4 translate-y-1/4" data-id="element-218" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] rounded-full bg-gold/5 blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-219" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-220">
        <div className="py-12 md:py-16 lg:py-20 lg:flex lg:items-center lg:gap-12" data-id="element-221">
          {/* Left Column: Copy */}
          <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0" data-id="element-222">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm font-semibold mb-5 text-[#B38B2E]" data-id="element-223">
              <span className="flex h-2 w-2 rounded-full mr-2 animate-pulse bg-[#B38B2E]" data-id="element-224" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-white tracking-tight mb-5 leading-[1.15]" data-id="element-225">
              {headline}
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-226">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm text-gray-300" data-id="element-227">
              <div className="flex items-center" data-id="element-228">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-229">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-230" />
                </svg>
                Flexible and Convenient
              </div>
              <div className="flex items-center" data-id="element-231">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-232">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-233" />
                </svg>
                Accredited Schools
              </div>
              <div className="flex items-center" data-id="element-234">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-235">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-236" />
                </svg>
                No Obligation
              </div>
            </div>
          </div>

          {/* Right Column: Form Card */}
          <div className="lg:w-1/2 flex justify-center lg:justify-end" data-id="element-237">
            <div className="w-full max-w-md" data-id="element-238">
              <form onSubmit={handleContinue} className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-239">
                {/* Card Header */}
                <div className="px-6 py-3.5 bg-[#A52A2A]" data-id="element-240">
                  <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-241">
                    Search Featured Schools
                  </h2>
                </div>

                <div className="p-6 space-y-4" data-id="element-242">
                  {/* Degree Level */}
                  <div data-id="element-243">
                    <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} aria-label="Select Degree Level" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-244">
                      <option value="" data-id="element-245">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-246">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-247">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-248">
                    <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} aria-label="Select Area of Study" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-249">
                      <option value="" data-id="element-250">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-251">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-252">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization */}
                  <div data-id="element-253">
                    <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} aria-label="Select Program" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-254">
                      <option value="" data-id="element-255">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-256">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-257">
                        {errors.specialization}
                      </p>}
                  </div>

                  {/* Continue Button */}
                  <button type="submit" className="w-full hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy bg-[#B38B2E]" data-id="element-258">
                    Search Programs
                  </button>
                </div>
              </form>

              {/* Social proof */}
              <p className="text-center text-gray-300 text-sm mt-4 italic" data-id="element-259">
                Used by{' '}
                <strong className="text-white font-semibold" data-id="element-260">
                  2M+ students
                </strong>{' '}
                to choose the right school.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}